import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  Play as StartIcon,
  CheckCircle2 as ApproveIcon,
  Rocket as ActivateIcon,
  RefreshCw as ReplanIcon,
  Moon as CloseIcon,
  Sun as TomorrowIcon,
  ChevronUp as ExpandLessIcon,
  ChevronDown as ExpandMoreIcon,
  Loader2,
} from 'lucide-react';

import { usePlanState } from '../hooks/usePlanState';
import { useAuth } from '../auth/AuthProvider';
import type { AnnualStrategicDriver, PlanStatus } from '../types/models';
import { planCopy } from '../copy/plan';

import StatusChip from '../components/StatusChip';
import NextActionCard from '../components/dashboard/NextActionCard';
import PriorityList from '../components/dashboard/PriorityList';
import RiskStrip from '../components/dashboard/RiskStrip';
import CapacityStrip from '../components/dashboard/CapacityStrip';
import WhyAffordance from '../components/ai/WhyAffordance';
import { buildRationaleMap } from '../utils/aiRationale';
import {
  deriveCapacity,
  deriveNextAction,
  derivePriorities,
  deriveRisks,
} from '../utils/dashboard';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { driversById } from '../utils/drivers';
import { useEntitlements } from '../context/EntitlementsContext';

// ---------------------------------------------------------------------------
// UnifiedPlanPage — Phase 2 task 2.1.
//
// One screen that renders the DailyPlan differently based on its status. The
// lifecycle is Draft → Proposed → Approved → Active → Adjusted → Closed, with
// EA and Executive seeing different actions (EA can send for approval and
// replan; only the Executive approves, activates, and closes).
//
// All data loading and transitions are delegated to `usePlanState(date)` so
// the rendering code here stays status-driven and side-effect-free.
//
// Migrated from MUI to Tailwind + shadcn/ui. The four shared dashboard
// sub-components (NextActionCard, PriorityList, RiskStrip, CapacityStrip)
// plus StatusChip and WhyAffordance now use shadcn primitives + Tailwind
// throughout — no MUI imports remain on this page or its children.
// ---------------------------------------------------------------------------

function explainerFor(status: PlanStatus | null): string | null {
  switch (status) {
    case 'Draft':
      return planCopy.explainDraft;
    case 'Proposed':
      return planCopy.explainProposed;
    case 'Approved':
      return planCopy.explainApproved;
    case 'Active':
      return planCopy.explainActive;
    case 'Adjusted':
      return planCopy.explainAdjusted;
    case 'Closed':
      return planCopy.explainClosed;
    default:
      return null;
  }
}

export default function UnifiedPlanPage() {
  const { user } = useAuth();
  const isEa = user?.role === 'ExecutiveAssistant';
  const driversIncluded = useEntitlements().has('strategicDrivers');

  const today = format(new Date(), 'yyyy-MM-dd');
  const prettyDate = format(new Date(), 'EEEE d MMMM');

  const {
    data,
    plan,
    status,
    initialLoading,
    busy,
    error,
    closeResult,
    canApprove,
    canActivate,
    canReplan,
    canClose,
    start,
    replan,
    approve,
    activate,
    close,
    planTomorrow,
    clearError,
    clearCloseResult,
    replanOutcome,
    clearReplanOutcome,
  } = usePlanState(today);

  const [briefingOpen, setBriefingOpen] = useState(true);
  const [reflectionContent, setReflectionContent] = useState('');
  const [reflectionInsights, setReflectionInsights] = useState('');
  const [reflectionAdjustments, setReflectionAdjustments] = useState('');

  // ---- Derivations ------------------------------------------------------
  const nextAction = useMemo(() => deriveNextAction(data), [data]);
  const priorities = useMemo(() => derivePriorities(data, 3), [data]);
  // Driver names only on plans with strategic drivers.
  const driverMap = useMemo(
    () => driversById(driversIncluded ? data?.strategicDrivers ?? [] : []),
    [data, driversIncluded],
  );
  const risks = useMemo(() => deriveRisks(data), [data]);
  const capacityLine = useMemo(
    () => (data ? deriveCapacity(data.capacity) : null),
    [data],
  );
  const rationaleMap = useMemo(() => buildRationaleMap(data), [data]);
  const nextActionRationale = nextAction?.workItemId
    ? rationaleMap.get(nextAction.workItemId)
    : undefined;

  const handleClose = async () => {
    await close({
      reflectionContent: reflectionContent.trim() || undefined,
      insights: reflectionInsights.trim() || undefined,
      adjustmentsMade: reflectionAdjustments.trim() || undefined,
    });
  };

  const hasAnyPlan = plan !== null;
  const hasFullData = data !== null;
  const explainer = explainerFor(status);

  // ---- Render -----------------------------------------------------------
  return (
    <div className="mx-auto max-w-[1200px] p-4 md:p-6">
      {/* Header row: date + status chip + role-appropriate transition actions. */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-serif text-2xl font-light tracking-tight">{prettyDate}</h1>
            {status && <StatusChip status={status} />}
            {/* How the day lines up with the drivers: only on plans with them. */}
            <WhyAffordance
              rationale={plan?.aiRationale}
              secondary={driversIncluded ? data?.aiStrategyAlignment : undefined}
              label="Why this shape of day"
            />
          </div>
          {plan?.dailyTheme && (
            <p className="mt-1 text-sm text-muted-foreground">{plan.dailyTheme}</p>
          )}
        </div>

        <ActionBar
          status={status}
          hasAnyPlan={hasAnyPlan}
          hasFullData={hasFullData}
          isEa={isEa}
          busy={busy}
          canApprove={canApprove}
          canActivate={canActivate}
          canReplan={canReplan}
          canClose={canClose}
          onStart={start}
          onReplan={replan}
          onApprove={approve}
          onActivate={activate}
          onPlanTomorrow={planTomorrow}
        />
      </div>

      {explainer && !initialLoading && (
        <p className="mb-6 text-sm text-muted-foreground">{explainer}</p>
      )}

      {error && (
        <Alert variant="error" className="mb-6">
          <div className="flex items-start justify-between gap-3">
            <AlertDescription className="flex-1">{error}</AlertDescription>
            <Button variant="ghost" size="sm" onClick={clearError}>Dismiss</Button>
          </div>
        </Alert>
      )}
      {replanOutcome && !error && (
        <Alert
          variant={replanOutcome.promotedCount > 0 ? 'success' : 'info'}
          className="mb-6"
        >
          <div className="flex items-start justify-between gap-3">
            <AlertDescription className="flex-1">
              {(() => {
                const verb = replanOutcome.kind === 'start' ? 'Plan generated' : 'Plan refreshed';
                const promoted = replanOutcome.promotedCount;
                const remaining = replanOutcome.backlogRemaining;
                const scheduled = replanOutcome.scheduledCount;
                if (promoted > 0) {
                  const promotedPart = `${promoted} ${promoted === 1 ? 'item' : 'items'} pulled from backlog`;
                  const tail = remaining > 0
                    ? ` — ${remaining} still in backlog ready to pull.`
                    : ' — backlog cleared.';
                  return `${verb}. ${promotedPart}${tail}`;
                }
                if (scheduled === 0 && remaining > 0) {
                  return `${verb}. No room to promote — the day is fully committed by meetings. ${remaining} ${remaining === 1 ? 'item is' : 'items are'} waiting in backlog; move a meeting or shorten the workday to make room.`;
                }
                if (remaining > 0) {
                  return `${verb}. No new items promoted — capacity already full. ${remaining} still in backlog.`;
                }
                return `${verb}. Backlog is clear.`;
              })()}
            </AlertDescription>
            <Button variant="ghost" size="sm" onClick={clearReplanOutcome}>Dismiss</Button>
          </div>
        </Alert>
      )}
      {closeResult && (
        <Alert variant="success" className="mb-6">
          <div className="flex items-start justify-between gap-3">
            <AlertDescription className="flex-1">
              Day closed. {closeResult.itemsToCarryForward}{' '}
              {closeResult.itemsToCarryForward === 1 ? 'item' : 'items'} carried forward.
            </AlertDescription>
            <Button variant="ghost" size="sm" onClick={clearCloseResult}>Dismiss</Button>
          </div>
        </Alert>
      )}

      {/* Handoff notes from EA — surface prominently for the executive on a
          Proposed plan. */}
      {status === 'Proposed' && plan?.handoffNotes && (
        <Card className="mb-6 border-l-[3px] border-l-primary">
          <CardContent className="py-4">
            <p className="mb-1 text-sm font-semibold">
              {planCopy.handoffNotesLabel}
              {plan.preparedBy ? ` — ${plan.preparedBy}` : ''}
            </p>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {plan.handoffNotes}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Status-driven body */}
      {initialLoading ? (
        <div className="py-12 text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      ) : !hasAnyPlan ? (
        <EmptyPlanState busy={busy} onStart={start} />
      ) : status === 'Closed' ? (
        <ClosedSummary plan={plan} closeResult={closeResult} />
      ) : !hasFullData ? (
        <PlanSummaryCard
          theme={plan?.dailyTheme}
          briefing={plan?.aiBriefing}
          risks={plan?.aiRisks}
          busy={busy}
          onLoadFull={start}
        />
      ) : (
        <PlanBody
          nextAction={nextAction}
          priorities={priorities}
          drivers={driverMap}
          risks={risks}
          capacity={capacityLine}
          briefing={data?.aiBriefing ?? plan?.aiBriefing ?? null}
          aiRisks={data?.aiRisks ?? plan?.aiRisks ?? null}
          briefingOpen={briefingOpen}
          onToggleBriefing={() => setBriefingOpen((v) => !v)}
          rationaleMap={rationaleMap}
          nextActionRationale={nextActionRationale?.rationale}
          nextActionStrategyLink={nextActionRationale?.strategyLink}
          onPlanTomorrow={planTomorrow}
        />
      )}

      {/* Reflection capture shown only when closing is in reach (Active /
          Adjusted), so the executive can jot a few lines alongside the Close
          day action instead of being sent to /reflections. */}
      {(status === 'Active' || status === 'Adjusted') && hasFullData && (
        <Card className="mt-8">
          <CardContent className="py-5">
            <h3 className="mb-1 text-sm font-semibold">{planCopy.closeDayHeading}</h3>
            <p className="mb-4 text-xs text-muted-foreground">
              Optional — leave blank to close without a reflection.
            </p>
            <div className="flex flex-col gap-3">
              <ReflectionField
                label={planCopy.closeDayPromptWorked}
                value={reflectionContent}
                onChange={setReflectionContent}
              />
              <ReflectionField
                label={planCopy.closeDayPromptSlipped}
                value={reflectionInsights}
                onChange={setReflectionInsights}
              />
              <ReflectionField
                label={planCopy.closeDayPromptTomorrow}
                value={reflectionAdjustments}
                onChange={setReflectionAdjustments}
              />
              <div>
                <Button
                  onClick={handleClose}
                  disabled={busy}
                  className="gap-1.5"
                >
                  <CloseIcon className="h-4 w-4" />
                  {planCopy.closeDayAction}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small label + textarea pair. Plain <label> rather than the shadcn Label
// primitive because there's nothing about these fields that needs Radix —
// they're regular form inputs the user clicks into directly.
// ---------------------------------------------------------------------------
function ReflectionField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
      {label}
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="text-sm text-foreground"
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Header action bar — the buttons visible depend on role + status. Keeping
// the logic here makes the main component's layout read top-down.
// ---------------------------------------------------------------------------

interface ActionBarProps {
  status: PlanStatus | null;
  hasAnyPlan: boolean;
  hasFullData: boolean;
  isEa: boolean;
  busy: boolean;
  canApprove: boolean;
  canActivate: boolean;
  canReplan: boolean;
  canClose: boolean;
  onStart: () => void;
  onReplan: () => void;
  onApprove: () => void;
  onActivate: () => void;
  onPlanTomorrow: () => void;
}

function ActionBar({
  status,
  hasAnyPlan,
  hasFullData,
  isEa,
  busy,
  canApprove,
  canActivate,
  canReplan,
  canClose,
  onStart,
  onReplan,
  onApprove,
  onActivate,
  onPlanTomorrow,
}: ActionBarProps) {
  if (!hasAnyPlan) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button onClick={onStart} disabled={busy} className="gap-1.5">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <StartIcon className="h-4 w-4" />}
          {planCopy.startMyDay}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {/* EA can send for approval on Draft; currently the backend emits
          Proposed directly, so this button is only shown if a Draft somehow
          exists. */}
      {isEa && status === 'Draft' && (
        <Button onClick={onStart} disabled={busy}>{planCopy.sendForApproval}</Button>
      )}

      {/* Executive-only: Approve on Proposed. EAs see a read-only hint. */}
      {canApprove && !isEa && (
        <Button onClick={onApprove} disabled={busy} className="gap-1.5">
          <ApproveIcon className="h-4 w-4" />
          {planCopy.approvePlan}
        </Button>
      )}

      {canActivate && !isEa && (
        <Button onClick={onActivate} disabled={busy} className="gap-1.5">
          <ActivateIcon className="h-4 w-4" />
          {planCopy.activatePlan}
        </Button>
      )}

      {canReplan && (
        <Button variant="outline" size="sm" onClick={onReplan} disabled={busy} className="gap-1.5">
          <ReplanIcon className="h-3.5 w-3.5" />
          {status === 'Active' || status === 'Adjusted'
            ? planCopy.replanMidDay
            : planCopy.adjustPlan}
        </Button>
      )}

      {canClose && !isEa && hasFullData && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            // Close flow handled via the reflection capture card at the
            // bottom of the page. This button scrolls to it.
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
          }}
          disabled={busy}
          className="gap-1.5"
        >
          <CloseIcon className="h-3.5 w-3.5" />
          {planCopy.closeDayAction}
        </Button>
      )}

      {status === 'Closed' && (
        <Button
          variant="outline"
          size="sm"
          onClick={onPlanTomorrow}
          disabled={busy}
          className="gap-1.5"
        >
          <TomorrowIcon className="h-3.5 w-3.5" />
          Plan tomorrow
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Body variants
// ---------------------------------------------------------------------------

function EmptyPlanState({
  busy,
  onStart,
}: {
  busy: boolean;
  onStart: () => void;
}) {
  return (
    <Card>
      <CardContent className="py-10 text-center">
        <h2 className="mb-1 text-lg font-semibold">{planCopy.noPlanHeading}</h2>
        <p className="mx-auto mb-6 max-w-[520px] text-sm text-muted-foreground">
          {planCopy.noPlanBody}
        </p>
        <Button onClick={onStart} disabled={busy} size="lg" className="gap-1.5">
          <StartIcon className="h-4 w-4" />
          {planCopy.startMyDay}
        </Button>
      </CardContent>
    </Card>
  );
}

// Shown when the plan record exists but the full schedule hasn't been loaded
// yet (the usual case on a return visit mid-day).
function PlanSummaryCard({
  theme,
  briefing,
  risks,
  busy,
  onLoadFull,
}: {
  theme?: string;
  briefing?: string | null;
  risks?: string | null;
  busy: boolean;
  onLoadFull: () => void;
}) {
  return (
    <Card>
      <CardContent className="py-6">
        {theme && (
          <p className="mb-2 text-sm font-semibold">
            {planCopy.dailyThemeLabel}: {theme}
          </p>
        )}
        {briefing && (
          <p className="mb-4 whitespace-pre-wrap text-sm text-muted-foreground">{briefing}</p>
        )}
        {risks && (
          <div className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
              {planCopy.risksLabel}
            </p>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{risks}</p>
          </div>
        )}
        <Button onClick={onLoadFull} disabled={busy} className="gap-1.5">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ReplanIcon className="h-4 w-4" />}
          Load current plan
        </Button>
      </CardContent>
    </Card>
  );
}

function PlanBody({
  nextAction,
  priorities,
  drivers,
  risks,
  capacity,
  briefing,
  aiRisks,
  briefingOpen,
  onToggleBriefing,
  rationaleMap,
  nextActionRationale,
  nextActionStrategyLink,
  onPlanTomorrow,
}: {
  nextAction: ReturnType<typeof deriveNextAction>;
  priorities: ReturnType<typeof derivePriorities>;
  drivers: Map<string, AnnualStrategicDriver>;
  risks: ReturnType<typeof deriveRisks>;
  capacity: ReturnType<typeof deriveCapacity> | null;
  briefing: string | null;
  aiRisks: string | null;
  briefingOpen: boolean;
  onToggleBriefing: () => void;
  rationaleMap?: ReturnType<typeof buildRationaleMap>;
  nextActionRationale?: string;
  nextActionStrategyLink?: string;
  onPlanTomorrow: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <NextActionCard
        action={nextAction}
        rationale={nextActionRationale}
        rationaleStrategyLink={nextActionStrategyLink}
        onPlanTomorrow={onPlanTomorrow}
      />

      <div className="flex flex-col gap-4 md:flex-row md:items-stretch">
        <div className="min-w-0 flex-[2]">
          <PriorityList items={priorities} rationaleMap={rationaleMap} drivers={drivers} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <RiskStrip risks={risks} aiRationale={aiRisks} />
          {capacity && <CapacityStrip capacity={capacity} />}
        </div>
      </div>

      {briefing && (
        <div className="mt-2">
          <button
            type="button"
            onClick={onToggleBriefing}
            className="flex w-full select-none items-center justify-between py-2 transition-colors hover:text-foreground"
            aria-expanded={briefingOpen}
          >
            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {planCopy.aiBriefingLabel}
            </span>
            {briefingOpen
              ? <ExpandLessIcon className="h-4 w-4 text-muted-foreground" />
              : <ExpandMoreIcon className="h-4 w-4 text-muted-foreground" />}
          </button>
          <div
            className={cn(
              'overflow-hidden transition-[max-height] duration-200 ease-out',
              briefingOpen ? 'max-h-[400px]' : 'max-h-0',
            )}
          >
            <Card>
              <CardContent className="py-4">
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{briefing}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function ClosedSummary({
  plan,
  closeResult,
}: {
  plan: ReturnType<typeof usePlanState>['plan'];
  closeResult: ReturnType<typeof usePlanState>['closeResult'];
}) {
  return (
    <Card>
      <CardContent className="py-5">
        <div className="mb-2 flex items-center gap-2">
          <Badge variant="muted">{planCopy.stateClosed}</Badge>
          <p className="text-sm font-semibold">{planCopy.explainClosed}</p>
        </div>
        {plan?.dailyTheme && (
          <p className="mb-2 text-sm text-muted-foreground">
            {planCopy.dailyThemeLabel}: {plan.dailyTheme}
          </p>
        )}
        {plan?.aiBriefing && (
          <p className="mb-2 whitespace-pre-wrap text-sm text-muted-foreground">{plan.aiBriefing}</p>
        )}
        {closeResult && (
          <p className="text-xs text-muted-foreground">
            {closeResult.itemsToCarryForward}{' '}
            {closeResult.itemsToCarryForward === 1 ? 'item' : 'items'} queued for tomorrow.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
